<template>
    <v-dialog v-model="dialogStatus" max-width="80vw" persistent>
        <v-form ref="form" v-model="validForm" @submit.prevent="submit">
            <v-card>
                <v-card-title class="ml-2 mr-2">
                    <v-icon left>
                        {{ 
                            dialogType == 'new' ? 'mdi-newspaper-plus' : 'mdi-pencil-box' 
                        }}
                    </v-icon>
                    {{ 
                        dialogType == 'new' ? 'New Purchase Order:' : 'Edit Purchase Order:' 
                    }}
                    <v-spacer />
                    <v-checkbox
                        v-model="purchaseOrderForm.is_generic"
                        label="Generic Purchase Order"
                        color="indigo"
                        hide-details
                    ></v-checkbox>
                </v-card-title>
                <v-card-text>
                    <v-row no-gutters class="ma-1">
                        <v-col cols="12" md="2">
                            <v-text-field
                                v-model="purchaseOrderForm.purchase_order_number"
                                label="P.O. Number"
                                :rules="[
                                    rules.required,
                                    rules.serverSide(
                                        uniquePO,
                                        'This PO number is already in use'
                                    )
                                ]"
                                @change="checkRepeatPO"
                                prepend-inner-icon="mdi-numeric"
                                readonly
                                disabled
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>

                        <v-col cols="12" md="3">
                            <v-autocomplete
                                v-model="purchaseOrderForm.driver_id"
                                :items="collections.drivers"
                                item-text="name"
                                item-value="id"
                                label="Driver"
                                :rules="purchaseOrderForm.is_generic || purchaseOrderForm.po_type == 'INVENTORY' ? [] : [rules.required]"
                                prepend-inner-icon="mdi-account"
                                outlined
                                dense
                                :disabled="!!purchaseOrderForm.is_generic || purchaseOrderForm.po_type == 'INVENTORY'"
                            ></v-autocomplete>
                        </v-col>

                        <v-col cols="12" md="2">
                            <v-autocomplete
                                v-model="purchaseOrderForm.region_id"
                                :items="collections.regions"
                                item-text="name"
                                item-value="id"
                                label="Region"
                                :rules="purchaseOrderForm.is_generic ? [] : [rules.required]"
                                prepend-inner-icon="mdi-earth"
                                outlined
                                dense
                                :disabled="!!purchaseOrderForm.is_generic"
                            ></v-autocomplete>
                        </v-col>

                        <v-col cols="12" md="3">
                            <v-autocomplete
                                v-model="purchaseOrderForm.maintenance_vendor_id"
                                :items="collections.vendors"
                                item-text="name"
                                item-value="id"
                                label="Vendor"
                                :rules="purchaseOrderForm.is_generic ? [] : [rules.required]"
                                prepend-inner-icon="mdi-store"
                                outlined
                                dense
                                :disabled="!!purchaseOrderForm.is_generic"
                            ></v-autocomplete>
                        </v-col>

                        <v-col cols="12" md="2">
                            <v-menu
                                v-model="dateMenu.purchase_order_date"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="purchaseOrderForm.approved_at"
                                        label="Purchase Order Date"
                                        prepend-inner-icon="mdi-calendar"
                                        single-line
                                        placeholder="YYYY-MM-DD"
                                        v-mask="'####-##-##'"
                                        outlined
                                        hint="Select the purchase order date"
                                        persistent-hint
                                        dense
                                        :rules="purchaseOrderForm.is_generic ? [] : [rules.required]"
                                        v-bind="attrs"
                                        v-on="on"
                                        :disabled="!!purchaseOrderForm.is_generic"
                                    ></v-text-field>
                                </template>
                                <v-date-picker
                                    v-model="purchaseOrderForm.approved_at"
                                    no-title
                                    @input="dateMenu.purchase_order_date = false"
                                ></v-date-picker>
                            </v-menu>
                        </v-col>
                    </v-row>

                    <v-row no-gutters class="ma-1">
                        <v-col cols="12" md="2">
                            <v-select
                                v-model="purchaseOrderForm.model_type"
                                :items="model_types"
                                label="Equipment Type"
                                item-text="name"
                                item-value="laravel"
                                :rules="purchaseOrderForm.is_generic ? [] : [rules.required]"
                                prepend-inner-icon="mdi-tire"
                                outlined
                                dense
                                :disabled="!!purchaseOrderForm.is_generic"
                                @change="handleModelType(purchaseOrderForm.model_type)"
                            ></v-select>
                        </v-col>
                        
                        <v-col cols="12" md="2">
                            <v-autocomplete
                                v-model="purchaseOrderForm.model_id"
                                :items="filteredModels"
                                item-text="name"
                                item-value="id"
                                label="Equipment"
                                :disabled="!!purchaseOrderForm.is_generic || purchaseOrderForm.po_type == 'INVENTORY' ? true : !purchaseOrderForm.model_type"
                                :rules="purchaseOrderForm.is_generic || purchaseOrderForm.po_type == 'INVENTORY' ? [] : [rules.required]"
                                prepend-inner-icon="mdi-truck"
                                outlined
                                dense
                            ></v-autocomplete>
                        </v-col>

                        <v-col cols="12" md="2">
                            <v-autocomplete
                                v-model="purchaseOrderForm.maintenance_expense_category_id"
                                :items="filteredMaintenanceCategories"
                                item-text="name"
                                item-value="id"
                                label="Category"
                                :rules="purchaseOrderForm.is_generic || purchaseOrderForm.po_type == 'INVENTORY' ? [] : [rules.required]"
                                prepend-inner-icon="mdi-format-list-bulleted-type"
                                outlined
                                dense
                                :disabled="!!purchaseOrderForm.is_generic || purchaseOrderForm.po_type == 'INVENTORY' ? true : !purchaseOrderForm.model_type"
                            ></v-autocomplete>
                        </v-col>

                        <v-col cols="12" md="2">
                            <v-autocomplete
                                v-model="purchaseOrderForm.authorized_by_id"
                                :items="collections.approvers"
                                item-text="name"
                                item-value="id"
                                :rules="purchaseOrderForm.is_generic ? [] : [rules.required]"
                                label="Authorized By"
                                prepend-inner-icon="mdi-account"
                                outlined
                                dense
                                :disabled="!!purchaseOrderForm.is_generic"
                            ></v-autocomplete>
                        </v-col>

                        <v-col cols="12" md="2">
                            <v-text-field
                                v-model="purchaseOrderForm.km"
                                hide-details
                                label="Km"
                                type="number"
                                prepend-inner-icon="mdi-numeric"
                                outlined
                                dense
                                :rules="purchaseOrderForm.is_generic || purchaseOrderForm.po_type == 'INVENTORY' ? [] : [rules.required]"
                                :disabled="!!purchaseOrderForm.is_generic || purchaseOrderForm.po_type == 'INVENTORY'"
                            ></v-text-field>
                        </v-col>

                        <v-col cols="12" md="2">
                            <v-text-field
                                v-model="purchaseOrderForm.estimated_cost"
                                hide-details
                                label="Estimated Cost"
                                type="number"
                                prepend-inner-icon="mdi-currency-usd"
                                :rules="purchaseOrderForm.is_generic ? [] : [rules.required]"
                                outlined
                                dense
                                :disabled="!!purchaseOrderForm.is_generic"
                            ></v-text-field>
                        </v-col>
                    </v-row>

                    <v-row no-gutters class="ma-1" v-if="purchaseOrderForm.is_generic">
                        <v-col cols="12" md="2">
                            <v-autocomplete
                                v-model="purchaseOrderForm.generic_authorized_by_id"
                                :items="collections.generic_approvers"
                                item-text="name"
                                item-value="id"
                                :rules="[rules.required]"
                                label="Generic Authorized By"
                                prepend-inner-icon="mdi-account"
                                outlined
                                dense
                            ></v-autocomplete>
                        </v-col>
                    </v-row>

                    <v-row no-gutters class="ma-1">
                        <v-col cols="12" md="12">
                            <v-textarea
                                v-model="purchaseOrderForm.description"
                                label="Description"
                                outlined
                                dense
                                hide-details
                                :rules="[rules.required]"
                                class="rounded-0"
                            />
                        </v-col>
                    </v-row>

                    <small class="red--text">*indicates required field</small>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="blue darken-1"
                        text
                        @click="handleClosePurchaseOrderDialog()"
                    >
                        Close
                    </v-btn>
                    <v-btn
                        v-if="dialogType == 'new'"
                        color="blue darken-1"
                        text
                        type="submit"
                        :disabled="!validForm"
                    >
                        Save
                    </v-btn>
                    <v-btn
                        v-if="dialogType == 'edit'"
                        color="blue darken-1"
                        text
                        type="submit"
                        :disabled="!validForm"
                    >
                        Update
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script>
import { validations } from '@/Utils/validation';
import { mask } from 'vue-the-mask';
import axios from 'axios';
// import { mapGetters } from 'vuex';

export default {
    name: 'PurchaseOrderDialog',
    directives: { mask },
    props: {
        dialogStatus: {
            type: Boolean,
            default: false
        },
        dialogType: String,
        purchaseOrderItem: Object,
        collections: Object,
        loading: Object
    },
    components: {

    },
    data: function () {
        return {
            validForm: true,
            rules: validations,
            purchaseOrderForm: {
                purchase_order_number: null,
                driver_id: null,
                region_id: null,
                maintenance_vendor_id: null,
                authorized_by_id: null,
                approved_at: null,
                model_type: null,
                model_id: null,
                maintenance_expense_category_id: null,
                km: null,
                description: null,
                estimated_cost: null,
                is_generic: false,
                generic_authorized_by_id: null,
                po_type: null,
            },
            dateMenu: {
                purchase_order_date: false,
            },
            model_types: [
                {
                    name: 'Truck',
                    value: 'truck',
                    axios: 'trucks',
                    laravel: 'App\\Truck',
                    id: 'TruckID'
                },
                {
                    name: 'Trailer',
                    value: 'trailer',
                    axios: 'trailers',
                    laravel: 'App\\Trailer',
                    id: 'TrailerId'
                },
                {
                    name: 'Inventory',
                    value: 'inventory',
                    axios: 'inventory',
                    laravel: 'inventory',
                    id: 'inventory'
                },
            ],
            uniquePO: true,
            prefix: ''
        }
    },
    mounted() {
        this.nextPONumber();
    },
    computed: {
        currentYear() {
            return new Date().getFullYear();
        },
        filteredMaintenanceCategories() {
            console.log('Filtered maintenance categories');

            if (this.purchaseOrderForm.model_type == 'App\\Trailer' && this.collections.categories) {
                let items = this.collections.categories.filter(type => type.name == 'Trailers');
                console.log(items);
                if (items.length) {
                    console.log('Cat items: ', items[0].maintenance_expense_categories);
                    return items[0].maintenance_expense_categories;
                }
            } else if (this.purchaseOrderForm.model_type == 'App\\Truck' && this.collections.categories) {
                let items = this.collections.categories.filter(type => type.name == 'Trucks');
                console.log(items);
                if (items.length) {
                    console.log('Cat items: ', items[0].maintenance_expense_categories);
                    return items[0].maintenance_expense_categories;
                }
            } else {
                return [];
            }
        },
        filteredModels() {
            console.log('Filtered models');

            if (this.purchaseOrderForm.model_type == 'App\\Truck') {
                return this.collections.trucks;
            } else if (this.purchaseOrderForm.model_type == 'App\\Trailer') {
                return this.collections.trailers;
            } else {
                return [];
            }
        }
    },
    watch: {
        dialogStatus(newVal) {
            if (this.dialogType == 'edit' && newVal) {
                console.log('Purchase order item: ', this.purchaseOrderItem);

                Object.keys(this.purchaseOrderForm).forEach(key => {
                    if (this.purchaseOrderItem.hasOwnProperty(key)) {
                        this.purchaseOrderForm[key] = this.purchaseOrderItem[key];
                    }
                });

                this.purchaseOrderForm.id = this.purchaseOrderItem.id;

                if (this.purchaseOrderItem.po_type == 'INVENTORY') {
                    this.purchaseOrderForm.model_type = 'inventory';
                }
            }
        },
    },
    methods: {
        async submit() {
            console.log('Submit form');
            console.log(this.purchaseOrderForm);

            if (!this.validForm) {
                this.$store.dispatch('setSnackbar', {
                    text: 'Please fill in all required fields',
                    color: 'error'
                });
                return;
            }

            let str = this.$store;
            str.state.isLoading = true;

            if (this.dialogType == 'new') {
                await this.nextPONumber();

                this.purchaseOrderForm.number = this.purchaseOrderForm.purchase_order_number.split('MAINT-')[1];
                this.purchaseOrderForm.year = this.currentYear;
                this.purchaseOrderForm.department = 'MNT';
                this.purchaseOrderForm.status = 'ACTIVE';

                axios
                    .post(
                        'v2/maintenance/purchase-orders', this.purchaseOrderForm
                    )
                    .then(response => {
                        console.log(response.data);
                        str.state.isLoading = false;
                        this.handleClosePurchaseOrderDialog();
                        this.$store.dispatch('setSnackbar', {
                            text: 'Purchase order successfully created',
                            color: 'success'
                        });
                        this.$emit('update:history');
                    })
                    .catch(error => {
                        str.state.isLoading = false;
                        console.error(error);
                    });
            } else if (this.dialogType == 'edit') {
                axios
                    .put(
                        'v2/maintenance/purchase-orders/' + this.purchaseOrderForm.id, this.purchaseOrderForm
                    )
                    .then(response => {
                        console.log(response.data);
                        str.state.isLoading = false;
                        this.handleClosePurchaseOrderDialog();
                        this.$store.dispatch('setSnackbar', {
                            text: 'Purchase order successfully updated',
                            color: 'success'
                        });
                        this.$emit('update:history');
                    })
                    .catch(error => {
                        str.state.isLoading = false;
                        console.error(error);
                    });
            }
        },
        handleClosePurchaseOrderDialog() {
            console.log('Close purchase order dialog');

            this.purchaseOrderForm = {
                purchase_order_number: null,
                driver_id: null,
                region_id: null,
                maintenance_vendor_id: null,
                authorized_by_id: null,
                approved_at: null,
                model_type: null,
                model_id: null,
                maintenance_expense_category_id: null,
                km: null,
                description: null,
                estimated_cost: null,
                is_generic: false,
                generic_authorized_by_id: null,
                po_type: null,
            };

            this.$refs.form.reset();
            this.$emit('close:dialog');

            this.nextPONumber();
        },
        checkRepeatPO() {
            let poNumber = this.purchaseOrderForm.purchase_order_number.split('MAINT-')[1];
            axios
                .get('/v2/maintenance/purchase-orders/check/' + poNumber)
                .then(response => {
                    if (response.data) {
                        this.uniquePO = false;
                    } else {
                        this.uniquePO = true;
                    }
                })
                .catch(error => {
                    console.log(error);
                });
        },
        async nextPONumber() {
            await axios
                .get('/v2/maintenance/purchase-orders/next')
                .then(response => {
                    // this.prefix = 'PO-' + this.currentYear + '-MNT-';
                    // this.prefix = 'MAINT-';
                    let po_number = 'MAINT-' + response.data;
                    this.purchaseOrderForm.purchase_order_number = po_number;
                    console.log('Purchase order number: ', this.purchaseOrderForm.purchase_order_number);
                })
                .catch(error => {
                    console.log(error);
                });
        },
        handleModelType(model_type) {
            if (model_type == 'inventory') {
                this.purchaseOrderForm.po_type = 'INVENTORY';
            } else if (model_type == 'App\\Truck') {
                this.purchaseOrderForm.po_type = 'TRUCK';
            } else if (model_type == 'App\\Trailer') {
                this.purchaseOrderForm.po_type = 'TRAILER';
            }
        }
    },
};
</script>

<style>

</style>